import React, { useState, ChangeEvent, useEffect, ReactNode } from 'react';

interface CheckBoxesProps {
    options?: string[],
    values?: string[],
    onValueChange?: (value: string[]) => void;
    getFriendlyNameForOptions: (value: string) => string;
    isDisabled?: boolean;
    disabledValues?: string[];
    enableLegacyToggle?: boolean;
    legacyToggleLabel?: string;
    legacyOptionPrefix?: string;
    isLegacyOption?: (value: string) => boolean;
}

const CheckBoxes: React.FC<CheckBoxesProps> = ({ values, options, onValueChange, getFriendlyNameForOptions, isDisabled, disabledValues, enableLegacyToggle, legacyToggleLabel, legacyOptionPrefix, isLegacyOption }) => {
    const [checkBoxValues, setCheckBoxValues] = useState(values || []);
    const [showLegacyOptions, setShowLegacyOptions] = useState(false);
    const expiredPrefix = legacyOptionPrefix ?? "[Expired]";

    useEffect(() => {
        setCheckBoxValues(values || []);
    }, [values]);

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const selectedValue = event.target.value;
        let updatedValues;

        if (checkBoxValues.includes(selectedValue)) {
            updatedValues = checkBoxValues.filter((value) => value !== selectedValue);
        } else {
            updatedValues = [...checkBoxValues, selectedValue];
        }

        updatedValues = updatedValues.filter((value) => !disabledValues?.includes(value));
        setCheckBoxValues(updatedValues);

        if (onValueChange) {
            onValueChange(updatedValues);
        }
    };

    const isInputDisabled = (value: string) => {
        return isDisabled || (disabledValues && disabledValues.includes(value));
    };

    const isLegacyOptionValue = (value: string) => {
        const isLegacyByOption = isLegacyOption?.(value);
        const friendlyName = getFriendlyNameForOptions(value);
        return isLegacyByOption === true || value.startsWith(expiredPrefix) ||
            friendlyName.startsWith(expiredPrefix) || friendlyName.startsWith("(Expired)");
    };

    const renderFriendlyNameForOption = (value: string): ReactNode => {
        const friendlyName = getFriendlyNameForOptions(value);
        const prefix = friendlyName.startsWith("(Expired)") ? "(Expired)" : expiredPrefix;
        const displayName = isLegacyOptionValue(value) && !friendlyName.startsWith(prefix)
            ? `${expiredPrefix} ${friendlyName}`
            : friendlyName;

        if (!displayName.startsWith(prefix)) {
            return displayName;
        }

        return (
            <>
                <span className="rohs-expired-label" style={{ color: "#b00020", fontWeight: 600 }}>{prefix}</span>
                {displayName.substring(prefix.length)}
            </>
        );
    };

    const hasLegacyOptions = (options ?? []).some(isLegacyOptionValue);
    const visibleOptions = enableLegacyToggle && !showLegacyOptions
        ? (options ?? []).filter(value => !isLegacyOptionValue(value) || checkBoxValues.includes(value))
        : (options ?? []);

    return (
        <div className="checkbox-question">
            {enableLegacyToggle && hasLegacyOptions &&
                <div className="checkbox rohs-legacy-toggle">
                    <label>
                        <input type="checkbox" autoComplete="off" checked={showLegacyOptions} onChange={e => setShowLegacyOptions(e.target.checked)} />
                        {legacyToggleLabel ?? "Show Legacy / Expired Exemptions"}
                    </label>
                </div>
            }
            {
                visibleOptions.map((value) =>
                <div className="checkbox" key={value}>
                    <label>
                            <input disabled={isInputDisabled(value)} type="checkbox" value={value} onChange={handleInputChange} checked={checkBoxValues.includes(value)} />{renderFriendlyNameForOption(value)}
                    </label>
                </div>)
            }
        </div>
    )
}

export default CheckBoxes;
